import React, { useEffect } from 'react';
import { useStore } from 'effector-react';
import { toFlag } from 'pano-utils';
import { useImmer } from 'use-immer';
import {
  type CategoryConfig,
  type GeneralID,
  ImageType,
  TemplateId,
} from '@types';
import { Collapse } from '@components/Collapse';
import Input from '@components/Input';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Title from '@components/Title';
import useTourEditing from '@hooks/useTourEditing';
import useInputHandlers from '@hooks/useInputHandelrs';
import useLangString from '@hooks/useLangString';
import { $templateId } from '@stores/dataStores';
import {
  $editingCategoryConfig,
  $editingHiddenIdSet,
} from '@stores/editingStores';
import FeatureToggle from '@components/FeatureToggle';
import { Dropdown } from '@components/Dropdown';
import { getTemplateConfig } from '@utils/templateConfig';
import PanelBase from '../PanelBase';
import { showSceneModal } from '../../category';
import { selectLibCBIcons } from '../../mediaLib';
import SceneSelector from '../components/SceneSelector';
import MainToggle from '../components/MainToggle';
import type { PanelBaseProps } from '../types';
import { switchTypeItems } from '../constants';

type HiddenSet = Set<GeneralID>;

interface Props extends Omit<PanelBaseProps<CategoryConfig>, 'onConfirm'> {
  batchMode?: boolean;
  defaultHiddenSet?: HiddenSet;
  onConfirm: (params: {
    config: CategoryConfig;
    hiddenIdSet: HiddenSet;
  }) => void;
}

function CategoryConfigBase({
  defaultValue,
  batchMode = false,
  defaultHiddenSet = new Set(),
  onConfirm,
}: Props) {
  const templateId = useStore($templateId);
  const [hiddenIdSet, setIdSet] = useImmer(defaultHiddenSet);

  const { state, setState, isChanged, defaultState } = useTourEditing(
    defaultValue,
    {
      enabledButton: 1,
      buttonMediaId: 0,
      buttonMediaUrl: '',
      buttonTitle: '',
      enabledColumn: 1,
      columnStyle: 0, // [[UNUSED]]
      switchCategoryShowFirstScene: 0,
      isDefaultShowColumn: 1,
      effect: 0,
    }
  );

  useEffect(() => {
    $editingHiddenIdSet.api.set(hiddenIdSet);
  }, [hiddenIdSet]);

  useEffect(() => {
    $editingCategoryConfig.api.set(state);
    return () => {
      $editingCategoryConfig.api.set(null);
    };
  }, [state]);

  const getConfig = getTemplateConfig(templateId);

  const lang = useLangString(
    {
      title: [
        templateId === TemplateId.realEstateA ? '场景' : '场景选择',
        'List',
      ],
    },
    [templateId]
  );

  const titleHandlers = useInputHandlers((val) =>
    setState((draft) => {
      draft.buttonTitle = val;
    })
  );

  const isOn = !!state.enabledButton;

  const handleIconSelect = async () => {
    const { libItems, sysItems } = await selectLibCBIcons();
    if (sysItems) {
      setState((draft) => {
        draft.buttonMediaId = 0;
        draft.buttonMediaUrl = sysItems[0].url;
      });
    } else if (libItems) {
      setState((draft) => {
        draft.buttonMediaId = libItems[0].id;
        draft.buttonMediaUrl = libItems[0].url;
      });
    }
  };

  const handleConfirm = () => {
    const config: CategoryConfig = isOn
      ? state
      : {
          ...defaultState,
          enabledButton: 0,
          enabledColumn: 0,
        };
    onConfirm({ config, hiddenIdSet });
  };

  const handleHideScene = async () => {
    const { idSet } = await showSceneModal({
      defaultSelected: hiddenIdSet,
      allowEmpty: true,
      multiple: true,
      title: '选择要隐藏的场景',
      subTitle: '隐藏后场景选择栏中的场景的缩略图将不会出现',
    });
    if (!idSet) return;
    setIdSet(idSet);
  };

  const renderInitScene = () => {
    return (
      <div className="mt-20">
        <div className="flex">
          <div>初始场景</div>
          <Tip className="ml-5 self-end text-sm">
            打开作品时，默认展示的场景
          </Tip>
        </div>
        <SceneSelector
          title="初始场景"
          className="mt-10"
          sceneId={state.initScene}
          onChange={(id) => {
            setState((draft) => {
              draft.initScene = id ?? 0;
            });
          }}
        />
      </div>
    );
  };

  const iconConfig = getConfig('categroyConfigIcon');

  return (
    <PanelBase
      title={<Title>场景选择</Title>}
      onConfirm={handleConfirm}
      isChanged={isChanged}
    >
      <MainToggle
        isOn={isOn}
        onToggle={(bool) => {
          setState((draft) => {
            draft.enabledButton = toFlag(bool);
            draft.enabledColumn = toFlag(bool);
          });
        }}
      />
      {isOn && (
        <>
          {!!iconConfig.icon && (
            <Collapse title="图标设置">
              <ImageContainer
                className="h-70"
                onDeleteClick={() =>
                  setState((draft) => {
                    draft.buttonMediaId = 0;
                    draft.buttonMediaUrl = '';
                  })
                }
                placeholder={
                  <Image
                    className="h-40 w-40"
                    src={iconConfig.icon}
                    type={ImageType.sysMedia}
                  />
                }
              >
                {!!state.buttonMediaUrl && (
                  <Image
                    src={state.buttonMediaUrl}
                    type={
                      state?.buttonMediaId === 0
                        ? ImageType.sysMedia
                        : ImageType.media
                    }
                    className="max-h-full max-w-full"
                  />
                )}
              </ImageContainer>
              <div className="mt-20 flex items-center justify-between">
                <Tip>建议尺寸：80 X 80</Tip>
                <Button size="sm" onClick={handleIconSelect}>
                  编辑图标
                </Button>
              </div>
            </Collapse>
          )}
          {!!iconConfig.title && (
            <Collapse title="标题设置">
              <Input
                placeholder={lang.title}
                maxLength={50}
                className="h-30"
                scheme="dark"
                tabIndex={-1}
                value={state?.buttonTitle}
                {...titleHandlers}
              />
            </Collapse>
          )}
          {!batchMode && (
            <Collapse title="场景设置">
              <div className="flex items-center justify-between">
                <div>隐藏场景缩略图</div>
                <Button size="sm" onClick={handleHideScene}>
                  选择场景
                </Button>
              </div>
              {renderInitScene()}
            </Collapse>
          )}
          <Collapse title="选择栏设置">
            <FeatureToggle
              title="切换分组时选中第一个场景"
              isOn={!!state.switchCategoryShowFirstScene}
              onToggle={(bool) =>
                setState((draft) => {
                  draft.switchCategoryShowFirstScene = toFlag(bool);
                })
              }
            />
            {getConfig('defaultShowColumn') && (
              <FeatureToggle
                title="打开作品时显示场景选择栏"
                isOn={!!state.isDefaultShowColumn}
                onToggle={(bool) =>
                  setState((draft) => {
                    draft.isDefaultShowColumn = toFlag(bool);
                  })
                }
              />
            )}
          </Collapse>
          <Collapse title="场景切换效果">
            <Dropdown
              items={switchTypeItems}
              selected={state?.effect ?? 0}
              onChange={(typeId) => {
                setState((draft) => {
                  draft.effect = typeId;
                });
              }}
            />
          </Collapse>
        </>
      )}
    </PanelBase>
  );
}

export default CategoryConfigBase;
